import React from 'react';
import { 
  Anchor, 
  Building2, 
  Home, 
  Users, 
  Umbrella, 
  ConciergeBell, 
  Sparkles, 
  CloudRain, 
  Trash2, 
  HardHat,
  Ship,
  Key,
  Palmtree,
  Warehouse,
  Droplets,
  CheckCircle
} from 'lucide-react';

const ClientsServices = () => {
  const clientTypes = [
    {
      icon: Ship,
      title: "Bateaux & Yachts",
      description: "Intervention à bord des bateaux de plaisance et yachts. Débouchage des WC marins, éviers, douches et systèmes d'évacuation.",
      features: ["WC marins", "Réservoirs eaux grises", "Pompes de cale", "Éviers cuisine"]
    },
    {
      icon: Anchor,
      title: "Ports de plaisance",
      description: "Service dédié aux capitaineries et gestionnaires de ports. Intervention rapide pour les plaisanciers du Port Vauban, Mandelieu, Golfe-Juan.",
      features: ["Port Vauban Antibes", "Port de Cannes", "Port Mandelieu", "Golfe-Juan"]
    },
    {
      icon: Building2,
      title: "Agences immobilières",
      description: "Partenaire des agences immobilières de la Côte d'Azur. Intervention rapide pour vos biens en gestion locative ou avant état des lieux.",
      features: ["Intervention rapide", "Devis détaillés", "Photos avant/après", "Facturation claire"]
    },
    {
      icon: Users,
      title: "Syndics & Copropriétés",
      description: "Entretien et débouchage des colonnes communes, regards, canalisations enterrées. Contrats d'entretien annuels pour copropriétés.",
      features: ["Colonnes communes", "Regards collecteurs", "Contrat entretien", "Urgences 24h/24"]
    },
    {
      icon: Key,
      title: "Airbnb & Locations",
      description: "Service express pour les propriétaires Airbnb et locations saisonnières. Intervention entre deux locataires, discrétion garantie.",
      features: ["Intervention express", "Entre 2 locataires", "Week-ends", "Discrétion"]
    },
    {
      icon: Palmtree,
      title: "Maisons de vacances",
      description: "Débouchage et remise en service des maisons de vacances. Intervention avant votre arrivée ou après votre départ.",
      features: ["Remise en service", "Hivernage", "Printemps", "Vérification complète"]
    },
    {
      icon: ConciergeBell,
      title: "Conciergeries",
      description: "Partenaire des conciergeries de luxe de Cannes, Mougins, Cap d'Antibes. Intervention discrète et professionnelle.",
      features: ["Service premium", "Discrétion absolue", "Disponibilité totale", "Facturation séparée"]
    },
    {
      icon: Sparkles,
      title: "Entreprises de nettoyage",
      description: "Collaboration avec les entreprises de nettoyage pour les interventions techniques. Débouchage après grand ménage.",
      features: ["Partenariat", "Intervention coordonnée", "Tarifs pros", "Rapidité"]
    }
  ];

  const specializedServices = [
    {
      icon: CloudRain,
      title: "Descentes pluviales",
      description: "Nettoyage et débouchage des descentes d'évacuation pluviale, gouttières, chéneaux. Prévention des dégâts des eaux.",
      features: ["Gouttières", "Chéneaux", "Regards EP", "Prévention inondations"]
    },
    {
      icon: Home,
      title: "Nettoyage toitures",
      description: "Nettoyage haute pression des toitures, terrasses, dalles. Élimination mousses, lichens, dépôts verts.",
      features: ["Toitures tuiles", "Terrasses", "Anti-mousse", "Traitement préventif"]
    },
    {
      icon: Trash2,
      title: "Nettoyage poubelles",
      description: "Désinfection et nettoyage haute pression des conteneurs poubelles, locaux poubelles, vide-ordures.",
      features: ["Conteneurs", "Locaux poubelles", "Vide-ordures", "Désinfection"]
    },
    {
      icon: Warehouse,
      title: "Vides sanitaires",
      description: "Intervention dans les vides sanitaires pour débouchage, pompage, assèchement. Accès difficiles maîtrisés.",
      features: ["Pompage", "Assèchement", "Débouchage", "Accès difficiles"]
    },
    {
      icon: HardHat,
      title: "WC de chantier",
      description: "Entretien et débouchage des sanitaires de chantier, bases vie, Algeco. Service régulier ou ponctuel.",
      features: ["Bases vie", "Algeco", "Entretien régulier", "Dépannage urgent"]
    },
    {
      icon: Droplets,
      title: "Curage préventif",
      description: "Contrats d'entretien annuels pour prévenir les engorgements. Curage programmé des canalisations.",
      features: ["Contrat annuel", "Curage préventif", "Rapport d'intervention", "Priorité urgences"]
    }
  ];

  return (
    <section id="clients" className="py-20 bg-gradient-to-b from-slate-50 via-white to-slate-50">
      <div className="max-w-7xl mx-auto px-4">
        {/* Section Header - Nos Clients */}
        <div className="text-center mb-16">
          <span className="inline-block bg-lime-100 text-lime-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Nos Clients
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Nous intervenons pour <span className="text-sky-600">tous les professionnels</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            De la plaisance aux copropriétés, des locations Airbnb aux conciergeries de luxe, nous adaptons notre service à chaque besoin
          </p>
        </div>

        {/* Client Types Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-20">
          {clientTypes.map((client, index) => {
            const Icon = client.icon;
            return (
              <div 
                key={index}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-slate-100"
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-sky-500 to-teal-500 flex items-center justify-center mb-4">
                  <Icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-800 mb-2">{client.title}</h3>
                <p className="text-gray-600 text-sm mb-4 leading-relaxed">{client.description}</p>
                <ul className="space-y-1">
                  {client.features.map((feature, idx) => (
                    <li key={idx} className="text-xs text-gray-500 flex items-center gap-2">
                      <CheckCircle size={12} className="text-lime-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* Section Header - Services Spécialisés */}
        <div className="text-center mb-16">
          <span className="inline-block bg-teal-100 text-teal-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Services Spécialisés
          </span>
          <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-4">
            Au-delà du <span className="text-teal-600">débouchage classique</span>
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            Des services complémentaires pour l'entretien complet de vos installations
          </p>
        </div>

        {/* Specialized Services Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {specializedServices.map((service, index) => {
            const Icon = service.icon;
            return (
              <div 
                key={index}
                className="bg-gradient-to-br from-sky-600 to-teal-600 rounded-2xl p-6 text-white shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="w-14 h-14 rounded-xl bg-white/20 flex items-center justify-center mb-4">
                  <Icon className="text-white" size={28} />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">{service.title}</h3>
                <p className="text-sky-100 text-sm mb-4 leading-relaxed">{service.description}</p>
                <ul className="grid grid-cols-2 gap-1">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="text-xs text-sky-200 flex items-center gap-1">
                      <span className="w-1.5 h-1.5 rounded-full bg-lime-400 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-lime-400 to-lime-500 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-4">
              Vous êtes un professionnel ?
            </h3>
            <p className="text-gray-800 mb-6">
              Contactez-nous pour établir un partenariat ou un contrat d'entretien adapté à vos besoins
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="tel:0744878763" className="bg-gray-900 hover:bg-gray-800 text-white font-bold px-8 py-4 rounded-xl transition-all inline-flex items-center justify-center gap-2">
                <span>07 44 87 87 63</span>
              </a>
              <a href="#contact" className="bg-white hover:bg-gray-50 text-gray-900 font-bold px-8 py-4 rounded-xl transition-all inline-flex items-center justify-center gap-2 border-2 border-gray-900">
                Demander un devis pro
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ClientsServices;
